import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Download, Share2, Trash2, FileText, FileImage, File as FileIcon, Loader2, Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import ShareDocumentModal from './ShareDocumentModal';

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MimeIcon({ mime }) {
  if (mime?.startsWith('image/')) return <FileImage size={16} />;
  if (mime === 'application/pdf') return <FileText size={16} />;
  return <FileIcon size={16} />;
}

export default function FilesTab() {
  const { currentOrg } = useOrg();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [shareTarget, setShareTarget] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true); setError(null);
    try { setDocs(await api.listDocuments(currentOrg.id)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [currentOrg]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (file) => {
    if (!file || !currentOrg) return;
    if (file.size > 50 * 1024 * 1024) { setError('File exceeds 50 MB limit'); return; }
    setUploading(true); setError(null);
    try { await api.uploadDocument(currentOrg.id, file); fetchDocs(); }
    catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const { url } = await api.getDocumentDownloadUrl(currentOrg.id, doc.id);
      window.open(url, '_blank');
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This can't be undone.`)) return;
    try { await api.deleteDocument(currentOrg.id, doc.id); fetchDocs(); }
    catch (err) { setError(err.message); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0]; if (f) handleUpload(f);
  };

  const filtered = docs.filter(d =>
    !search || d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Drop zone + upload button */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mb-5 p-6 rounded-[14px] border-2 border-dashed transition-all ${
          dragOver
            ? 'border-accent bg-accent-glow'
            : 'border-theme bg-elevated hover:border-strong'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-[42px] h-[42px] rounded-[12px] bg-accent-glow flex items-center justify-center text-accent-light flex-shrink-0">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-primary-c">
                {uploading ? 'Uploading…' : 'Drop a file here or click to upload'}
              </div>
              <div className="text-[11px] text-muted-c">Any file type, up to 50 MB.</div>
            </div>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow-lg disabled:opacity-50"
          >
            Choose file
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-[10px] bg-danger/10 border border-danger/30 text-[12px] text-danger flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}

      {/* Search */}
      {docs.length > 0 && (
        <div className="mb-4 relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-c" />
          <input
            type="text"
            placeholder="Search by file name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 bg-tertiary border border-strong rounded-[10px] text-[13px] text-primary-c focus:outline-none focus:border-[#0DA7CA]"
          />
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-muted-c text-[12px] flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading documents…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-[48px] h-[48px] rounded-[14px] bg-tertiary mx-auto mb-3 flex items-center justify-center text-muted-c">
            <FileText size={20} />
          </div>
          <p className="text-[13px] text-primary-c font-medium mb-1">
            {docs.length === 0 ? 'No documents yet' : 'No matches'}
          </p>
          <p className="text-[12px] text-muted-c">
            {docs.length === 0 ? 'Upload your first file using the area above.' : 'Try a different search.'}
          </p>
        </div>
      ) : (
        <div className="bg-elevated border border-theme rounded-[14px] overflow-hidden">
          {filtered.map((doc, i) => {
            const liveShares = (doc.shares || []).filter(s => !s.revoked_at);
            const uploaderName = doc.uploader
              ? `${doc.uploader.first_name || ''} ${doc.uploader.last_name || ''}`.trim() || doc.uploader.email
              : '';
            return (
              <div key={doc.id} className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-theme' : ''}`}>
                <div className="w-[36px] h-[36px] rounded-[10px] bg-tertiary flex items-center justify-center text-muted-c flex-shrink-0">
                  <MimeIcon mime={doc.mime_type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-primary-c truncate">{doc.name}</div>
                  <div className="text-[11px] text-muted-c flex items-center gap-2 flex-wrap">
                    <span>{fmtBytes(doc.size_bytes)}</span>
                    <span>·</span>
                    <span>{format(new Date(doc.created_at), 'MMM d, yyyy')}</span>
                    {uploaderName && <><span>·</span><span>by {uploaderName}</span></>}
                    {liveShares.length > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-accent-light font-medium">
                          Shared with {liveShares.length} org{liveShares.length === 1 ? '' : 's'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleDownload(doc)} title="Download"
                    className="p-2 rounded-[8px] text-muted-c hover:text-primary-c hover:bg-tertiary transition-all">
                    <Download size={14} />
                  </button>
                  <button onClick={() => setShareTarget(doc)} title="Share with a partner org"
                    className="p-2 rounded-[8px] text-muted-c hover:text-accent-light hover:bg-accent-glow transition-all">
                    <Share2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(doc)} title="Delete"
                    className="p-2 rounded-[8px] text-muted-c hover:text-danger hover:bg-danger/10 transition-all">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {shareTarget && (
        <ShareDocumentModal
          document={shareTarget}
          onClose={() => setShareTarget(null)}
          onShared={() => { setShareTarget(null); fetchDocs(); }}
        />
      )}
    </div>
  );
}
